// ===========================================
// Line Item Week Row
// ===========================================
// One enrollment in the manual-invoice week selector: shows the lesson/student,
// each billable bucket's (standard, or hybrid group/individual/band) per-week
// price — the pricing-matrix price, read-only unless the admin switches on
// "Custom price" for that bucket — and one of two ways to choose weeks:
//  - "Starting week" (default): ONE week list for the whole class; the admin
//    clicks the week the student started and every bucket's weeks from then to
//    the end of term are selected (applyStartWeek). A hybrid needs no split grids.
//  - "Select weeks": a WeekTilePicker per bucket, toggled week by week.
// Switching between them never changes what is billed; a hand-edited selection
// that no longer matches its start week is called out (customSelection).
// Controlled by the parent dialog, which owns the SelectedEnrollment state. The
// SERVER prices and words the lines; the start date shown here is the one the
// server's preview returned for this row.

import {
  Alert,
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  Switch,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import WeekTilePicker from '../booking/WeekTilePicker';
import { BillableEnrollment } from '../../api/invoices.api';
import {
  CustomSelection,
  SelectedEnrollment,
  SelectedBucket,
  WeekSelectionMode,
  applyStartWeek,
  customSelection,
  effectiveRate,
  selectedUnion,
  setSelectionMode,
  startWeekWriteBack,
  storedEffectiveStart,
  unionSessionWeeks,
} from './lineItemWeekBuilder';

interface LineItemWeekRowProps {
  enrollment: BillableEnrollment;
  termStartDate: string;
  termEndDate: string;
  value: SelectedEnrollment;
  onChange: (next: SelectedEnrollment) => void;
  onRemove: () => void;
  /**
   * The "start date dd/mm/yyyy" the server's preview prints for this row: null =
   * none (no starting week, or a full-term start), undefined = not priced yet.
   */
  startDate?: string | null;
  /**
   * Offer "Save as {student}'s start week" when the chosen starting week differs from the
   * stored one. Create-invoice dialog only; default false (the amend / edit dialogs never
   * write to an enrolment).
   */
  allowSaveStartWeek?: boolean;
}

function bucketHeading(kind: SelectedBucket['kind']): string {
  switch (kind) {
    case 'GROUP':
      return 'Group weeks';
    case 'INDIVIDUAL':
      return 'Individual weeks';
    case 'BAND':
      return 'Band weeks';
    default:
      return 'Weeks';
  }
}

const weekList = (weeks: number[]) => `Wk ${weeks.join(', ')}`;

/** "Custom selection: …" — what a hand-edited selection bills, in words. */
function describeCustom(startWeek: number | null, custom: CustomSelection): string {
  if (startWeek == null) {
    return `Custom selection: weeks picked individually (${weekList(custom.added)}).`;
  }
  const differences = [
    custom.unticked.length > 0 ? `${weekList(custom.unticked)} not billed` : null,
    custom.added.length > 0 ? `${weekList(custom.added)} also billed` : null,
  ].filter(Boolean);
  return `Custom selection: from Week ${startWeek}, but ${differences.join(' and ')}.`;
}

export default function LineItemWeekRow({
  enrollment,
  termStartDate,
  termEndDate,
  value,
  onChange,
  onRemove,
  startDate,
  allowSaveStartWeek = false,
}: LineItemWeekRowProps) {
  const updateBucket = (index: number, patch: Partial<SelectedBucket>) => {
    const buckets = value.buckets.map((b, i) => (i === index ? { ...b, ...patch } : b));
    onChange({ ...value, buckets });
  };

  const toggleWeek = (bucketIndex: number, week: number) => {
    const bucket = value.buckets[bucketIndex];
    const selectedWeeks = bucket.selectedWeeks.includes(week)
      ? bucket.selectedWeeks.filter((w) => w !== week)
      : [...bucket.selectedWeeks, week].sort((a, b) => a - b);
    updateBucket(bucketIndex, { selectedWeeks });
  };

  const allSessionWeeks = unionSessionWeeks(enrollment, value.lastWeek);
  const lastSessionWeek = allSessionWeeks[allSessionWeeks.length - 1];
  const startWeek = value.startWeek;
  const custom = customSelection(value);
  // The class's own window: the tiles outside it are already unselectable (sessionWeeks are
  // bounded server-side); these give them the right REASON ("Class starts in week 5").
  const windowFirst = enrollment.classStartWeek ?? undefined;
  const windowLast = enrollment.classEndWeek ?? undefined;
  const studentFirstName = enrollment.studentName.split(' ')[0];
  const showSaveStartWeek = allowSaveStartWeek && startWeekWriteBack(value).changed;
  // A stored start AFTER the last session week seeds nothing (there is no tile to click), so the
  // row would open silently empty: say why instead (whole-feature /qa L8).
  const storedStart = storedEffectiveStart(enrollment);
  const startsAfterLastSession = lastSessionWeek !== undefined && storedStart > lastSessionWeek;

  // Clicking a week bills from there to the end of term. Clicking the chosen
  // start again clears it — unless the selection was hand-edited, when the click
  // resets it to "from this week" instead of throwing the edit away.
  const pickStartWeek = (week: number) =>
    onChange(applyStartWeek(value, !custom && week === startWeek ? null : week));

  const startCaption = () => {
    if (startWeek == null) {
      return 'Click the week the student started. Every week this class runs from then to the end of term is billed.';
    }
    const range = `Billing from Week ${startWeek}${startDate ? ` ${startDate}` : ''} to the end of term (Week ${lastSessionWeek}).`;
    // undefined = the server has not priced this selection yet; say nothing.
    const dateNote =
      startDate === undefined
        ? ''
        : startDate
          ? ` The invoice line shows "start date ${startDate}".`
          : ' That is the full term, so no start date is printed.';
    return `${range}${dateNote} Click Week ${startWeek} again to clear, or use "Select weeks" to untick a week.`;
  };

  // Display only — the invoice total is the server's (the dialog's preview).
  const lineTotal = value.buckets.reduce(
    (sum, b) => sum + b.selectedWeeks.length * (effectiveRate(b) || 0),
    0
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {enrollment.studentName} — {enrollment.lessonName}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
            <Chip size="small" label={enrollment.lessonType} />
            {enrollment.programLevelId && (
              <Chip size="small" variant="outlined" label={enrollment.programLevelName} />
            )}
            {enrollment.instrumentName && (
              <Chip size="small" variant="outlined" label={enrollment.instrumentName} />
            )}
          </Box>
        </Box>
        <Tooltip title="Remove this lesson from the invoice">
          <IconButton size="small" onClick={onRemove} aria-label="Remove lesson">
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={value.mode}
        onChange={(_e, mode: WeekSelectionMode | null) => {
          if (mode) onChange(setSelectionMode(value, mode));
        }}
        aria-label="How to choose weeks"
        sx={{ mt: 2 }}
      >
        <ToggleButton value="START_WEEK">Starting week</ToggleButton>
        <ToggleButton value="MANUAL">Select weeks</ToggleButton>
      </ToggleButtonGroup>

      {value.mode === 'START_WEEK' && (
        <Box sx={{ mt: 2 }} data-testid="start-week-picker">
          {allSessionWeeks.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              This lesson runs no sessions this term.
            </Typography>
          ) : (
            <>
              {custom ? (
                <Alert severity="info" sx={{ mb: 1 }}>
                  {describeCustom(startWeek, custom)} Click a week to bill every week from there to
                  the end of term instead.
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {startCaption()}
                </Typography>
              )}
              <WeekTilePicker
                termStartDate={termStartDate}
                termEndDate={termEndDate}
                multiSelect
                // What is actually billed (the union of the buckets), never a
                // recomputed range — so a hand-edited selection shows as it is.
                selectedWeeks={selectedUnion(value)}
                onToggleWeek={pickStartWeek}
                sessionWeeks={allSessionWeeks}
                // Billing is after the fact: see the per-bucket picker below.
                allowPast
                highlightPast={false}
                windowFirst={windowFirst}
                windowLast={windowLast}
              />
            </>
          )}
        </Box>
      )}

      {value.buckets.map((bucket, index) => {
        const sessionWeeks = enrollment.buckets[index]?.sessionWeeks ?? [];
        const noSessions = sessionWeeks.length === 0;
        const isManual = value.mode === 'MANUAL';
        const n = bucket.selectedWeeks.length;
        return (
          <Box key={bucket.kind} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {bucketHeading(bucket.kind)}{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  {isManual
                    ? `(${n} of ${sessionWeeks.length} selected)`
                    : n > 0
                      ? `(${n} ${n === 1 ? 'week' : 'weeks'}: ${weekList(bucket.selectedWeeks)})`
                      : startWeek == null
                        ? '(choose a starting week)'
                        : `(none from Week ${startWeek})`}
                </Typography>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {/* The matrix price bills unless the admin deliberately switches on a
                    custom one for THIS bucket (a hybrid can keep its group price and
                    override only the clinic). The server re-reads the matrix price
                    itself; only a switched-on custom price is sent. */}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={bucket.customRate != null}
                      onChange={(e) =>
                        updateBucket(index, {
                          customRate: e.target.checked ? bucket.unitPrice : null,
                        })
                      }
                      inputProps={{
                        'aria-label': `Custom price for ${bucketHeading(bucket.kind).toLowerCase()}`,
                      }}
                    />
                  }
                  label={<Typography variant="caption">Custom price</Typography>}
                  sx={{ mr: 0 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Price / week"
                  disabled={bucket.customRate == null}
                  value={
                    bucket.customRate != null
                      ? bucket.customRate === 0
                        ? ''
                        : bucket.customRate
                      : bucket.unitPrice === 0
                        ? ''
                        : bucket.unitPrice
                  }
                  onChange={(e) =>
                    updateBucket(index, { customRate: parseFloat(e.target.value) || 0 })
                  }
                  helperText={bucket.customRate == null ? 'From pricing matrix' : 'Custom'}
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ width: 140 }}
                />
              </Box>
            </Box>
            {!isManual ? null : noSessions ? (
              <Typography variant="caption" color="text.secondary">
                This lesson runs no {bucketHeading(bucket.kind).toLowerCase()} this term.
              </Typography>
            ) : (
              <WeekTilePicker
                termStartDate={termStartDate}
                termEndDate={termEndDate}
                multiSelect
                selectedWeeks={bucket.selectedWeeks}
                onToggleWeek={(week) => toggleWeek(index, week)}
                sessionWeeks={sessionWeeks}
                // Billing is for weeks ALREADY taught as often as future ones: a student
                // who joined in week 5 is invoiced after the fact, and a term may be
                // billed on its last day. The past-week lock is a BOOKING rule inherited
                // from the shared picker, not a billing one: the backend puts no date
                // bound on invoiced weeks. No-session weeks stay disabled regardless.
                // Past weeks are routine here, so they are not styled as a warning.
                allowPast
                highlightPast={false}
                windowFirst={windowFirst}
                windowLast={windowLast}
              />
            )}
          </Box>
        );
      })}

      {startsAfterLastSession && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
          data-testid="starts-after-last-session"
        >
          {studentFirstName} starts in Week {storedStart}, after this class&apos;s last session — nothing to bill.
        </Typography>
      )}

      {showSaveStartWeek && (
        <FormControlLabel
          sx={{ display: 'block', mt: 1 }}
          control={
            <Checkbox
              size="small"
              checked={value.saveStartWeek === true}
              onChange={(e) => onChange({ ...value, saveStartWeek: e.target.checked })}
            />
          }
          label={
            <Typography variant="body2">
              Save as {studentFirstName}&apos;s start week
            </Typography>
          }
        />
      )}

      {value.mode === 'MANUAL' && startDate && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          The invoice line shows &quot;start date {startDate}&quot; (the first billed week).
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Typography variant="body2" fontWeight={600}>
          Line total:{' '}
          {new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(lineTotal)}
        </Typography>
      </Box>
    </Paper>
  );
}
